import React, { useCallback, useMemo } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import USGAScoreRow from "../USGAScoreRow";

export interface USGAHoleDetail {
  holeNumber: number;
  length?: number;
  par?: number;
  allocation?: number;
  rawScore?: number;
  adjustedGrossScore?: number;
  putts?: number;
  gir?: number;
  fairwayHit?: number;
}

interface Props {
  holesData: USGAHoleDetail[];
  hasAdvancedStats: boolean;
}

const USGAScoreCardTable = ({
  holesData,
  hasAdvancedStats
}: Props) => {
  const { t } = useTranslation();

  const tableTitleHead = useMemo(() => <div className={styles.title_head_cell}>{t('Hole')}</div>, []);

  const tableTitles = useMemo(() => {
    const baseTitles = [t('Yards'), t('Par'), t('S.I.'), t('Score'), t('Adj. Score')];
    const advancedTitles = [...baseTitles, t('Putts'), t('GIR'), t('Driving Acc.')];

    const titles = hasAdvancedStats ? advancedTitles : baseTitles;

    return titles.map((title, index) => (
      <div
        key={index}
        className={cls(styles.title_cell, { [styles.last_cell]: index === titles.length - 1 })}
      >
        {title}
      </div>
    ));
  }, [hasAdvancedStats]);

  const tableHead = useMemo(() => {
    const headElements = [
      ...Array(9).fill(0).map((_, i) => (i + 1).toString()),
      'OUT',
      ...Array(9).fill(0).map((_, i) => (i + 10).toString()),
      'IN',
      'TOTAL',
    ];

    return headElements.map((headElement, index) => (
      <div
        key={index}
        className={cls(styles.head_element_wrapper, { [styles.last_head_element_cell]: index === headElements.length - 1 })}
      >
        {headElement}
      </div>
    ));
  }, []);

  const getDataForHoleNumber = useCallback(
    (holeNumber: number) => holesData.find((data) => data.holeNumber === holeNumber),
    [holesData],
  );

  const getDataForIndex = useCallback((index: number, data: USGAHoleDetail) => {
    if (!data) {
      return undefined;
    }

    switch (index) {
      case 0:
        return data.length;
      case 1:
        return data.par;
      case 2:
        return data.allocation;
      case 3:
        return data.rawScore;
      case 4:
        return data.adjustedGrossScore;
      case 5:
        return data.putts;
      case 6:
        return data.gir;
      case 7:
        return data.fairwayHit;
      default:
        return undefined;
    }
  }, []);

  const renderSign = useCallback((index: number, data: USGAHoleDetail) => {
    const signValue = index === 6 ? data?.gir : data?.fairwayHit;

    switch (signValue) {
      case 0:
        return (
          <div className={cls(styles.red_circle, styles.left)}>
            <i className="ico ico-chevron" />
          </div>
        );
      case 1:
        return (
          <div className={cls(styles.red_circle, styles.right)}>
            <i className="ico ico-chevron" />
          </div>
        );
      case 2:
        return (
          <div className={cls(styles.red_circle, styles.top)}>
            <i className="ico ico-chevron" />
          </div>
        );
      case 3:
        return (
          <div className={cls(styles.red_circle, styles.down)}>
            <i className="ico ico-chevron" />
          </div>
        );
      case 4:
        return (
          <div className={styles.green_circle}>
            <i className="ico ico-check-new" />
          </div>
        );
      default:
        return null;
    }
  }, []);

  const tableData = useMemo(() => {
    const result: React.ReactNode[][] = [];
    const rowCount = hasAdvancedStats ? 8 : 5;

    // OUT Data
    for (let i = 0; i < rowCount; i++) {
      const rowData: React.ReactNode[] = [];
      let sum = 0;
      let totalSum = 0;

      for (let hole = 1; hole <= 10; hole++) {
        const data = getDataForHoleNumber(hole);
        const cellData = getDataForIndex(i, data);

        if (hole !== 10) {
          if (i > 5) {
            if (cellData === 4) {
              sum += 1;
            }
          } else {
            sum += cellData ?? 0;
          }
        }
        if (hole === 10) {
          const total = i === 2 ? ' ' : sum;
          totalSum += sum;

          rowData.push(
            <div key={`out-total-${i}`} className={cls(styles.table_data_wrapper, styles.background_white)}>
              {total}
            </div>,
          );
        } else {
          rowData.push(
            <div key={`out-hole-${hole}-${i}`} className={cls(styles.table_data_wrapper, styles.background_white)}>
              <USGAScoreRow
                showOverlay={i === 3 || i === 4}
                par={getDataForIndex(1, data)}
                score={cellData}
              >
                {i > 5 ? renderSign(i, data) : i === 5 ? cellData : cellData ?? '-'}
              </USGAScoreRow>
            </div>,
          );
        }
      }

      sum = 0;
      // IN Data
      for (let hole = 10; hole <= 20; hole++) {
        const data = getDataForHoleNumber(hole);
        const cellData = getDataForIndex(i, data);

        if (hole <= 18) {
          if (i > 5) {
            if (cellData === 4) {
              sum += 1;
            }
          } else {
            sum += cellData ?? 0;
          }
        }
        if (hole === 19) {
          const total = i === 2 ? ' ' : sum;
          totalSum += sum;
          rowData.push(
            <div key={`in-total-${i}`} className={cls(styles.table_data_wrapper, styles.background_white)}>
              {total}
            </div>,
          );
        } else if (hole === 20) {
          const total = i === 2 ? ' ' : totalSum;
          rowData.push(
            <div
              key={`total-total-${i}`}
              className={cls(styles.table_data_wrapper, styles.background_white, { [styles.border_radius_right_bottom]: i === rowCount - 1 })}
            >
              {total}
            </div>,
          );
        } else {
          rowData.push(
            <div key={`in-hole-${hole}-${i}`} className={cls(styles.table_data_wrapper, styles.background_white)}>
              <USGAScoreRow
                showOverlay={i === 3 || i === 4}
                par={getDataForIndex(1, data)}
                score={cellData}>
                {i > 5 ? renderSign(i, data) : i === 5 ? cellData : cellData ?? '-'}
              </USGAScoreRow>
            </div>,
          );
        }
      }

      result.push(rowData);
    }

    return result;
  }, [getDataForHoleNumber, getDataForIndex, hasAdvancedStats, renderSign]);

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{tableTitleHead}</th>
            {tableHead.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableTitles.map((title, rowIndex) => (
            <tr key={rowIndex}>
              <td>{title}</td>
              {tableData[rowIndex].map((cell, colIndex) => (
                <td key={colIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default USGAScoreCardTable;
